import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Heart, Users, Target, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';

gsap.registerPlugin(ScrollTrigger);

const AboutUs = () => {
  const sectionRef = useRef(null);
  const visionRef = useRef(null);
  const valuesRef = useRef([]);

  const values = [
    {
      icon: Heart,
      title: 'Purpose-Driven',
      description: 'Every box supports a good cause. We believe business can create community impact.',
      color: 'bg-pink-500'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'We empower local groups to raise funds for their own initiatives, giving where it matters most.',
      color: 'bg-teal-500'
    },
    {
      icon: Target,
      title: 'Quality & Care',
      description: 'Our doughnuts are freshly made, premium in quality, and handled with care.',
      color: 'bg-amber-500'
    },
    {
      icon: Sparkles,
      title: 'Joyful Giving',
      description: 'Fundraising should be simple, fun, and rewarding for everyone involved',
      color: 'bg-pink-500'
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate vision box
      gsap.fromTo(visionRef.current,
        { scale: 0.8, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          ease: 'back.out(1.7)',
          scrollTrigger: {
            trigger: visionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        }
      );

      // Animate value cards
      valuesRef.current.forEach((card, index) => {
        gsap.fromTo(card,
          { y: 60, opacity: 0, rotation: -5 },
          {
            y: 0,
            opacity: 1,
            rotation: 0,
            duration: 0.7,
            delay: index * 0.15,
            ease: 'back.out(1.4)',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 px-4 bg-gradient-to-br from-white via-amber-50 to-pink-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About Dough-nate
          </h2>
          <p className="text-xl text-gray-600">
             A social enterprise reimagining how communities raise funds
          </p>
          <div className="flex justify-center space-x-2 mt-4">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  backgroundColor: ['#ec4899', '#14b8a6', '#fbbf24', '#f97316', '#ec4899'][i]
                }}
              />
            ))}
          </div>
        </div>

        {/* Vision Statement */}
        <div ref={visionRef} className="mb-20">
          <Card className="bg-gradient-to-br from-amber-100 via-pink-100 to-amber-100 border-4 border-amber-300 shadow-2xl overflow-hidden relative">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-pink-300 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            
            <CardContent className="p-8 md:p-12 relative z-10">
              <div className="text-center">
                <div className="inline-block mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <Heart className="text-white" size={40} fill="white" />
                  </div>
                </div>
                
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Our Vision
                </h3>
                
                <p className="text-xl md:text-2xl text-gray-800 leading-relaxed max-w-4xl mx-auto mb-6">
                  Our vision is to make <span className="font-bold text-amber-700">Fundraising Sweeter</span> — by helping charities, schools, and community groups raise more through irresistible treats. Our mission is to supply premium doughnuts that empower organisations to generate funds easily, ethically, and joyfully, one box at a time. Built on the belief that business and charity can work hand in hand, Doughnate bridges the gap between delicious products and meaningful giving.

                </p>
                
                {/* <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                  Doughnate was created to make fundraising easier, more enjoyable, and more impactful. We partner with charities, schools, and community groups, supplying them with high-quality doughnuts they can resell to support their own projects and initiatives. Every box sold helps create change. Every bite helps a good cause grow.

                </p> */}

                {/* Sprinkle decoration */}
                <div className="flex justify-center space-x-2 mt-8">
                  {[...Array(12)].map((_, i) => (
                    <span
                      key={i}
                      className="inline-block w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: ['#ec4899', '#14b8a6', '#fbbf24'][i % 3]
                      }}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Our Values */}
        <div className="mb-12">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            What We Stand For
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  ref={el => valuesRef.current[index] = el}
                  className="value-card"
                >
                  <Card className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-2 bg-white border-2 border-gray-200 hover:border-amber-300">
                    <CardContent className="p-6 text-center">
                      <div className={`w-16 h-16 ${value.color} rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 hover:scale-110 hover:rotate-12 shadow-lg`}>
                        <Icon className="text-white" size={32} />
                      </div>
                      <h4 className="font-bold text-xl text-gray-900 mb-3">{value.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                      
                      {/* Sprinkle decoration */}
                      <div className="flex justify-center space-x-1 mt-4">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{
                              backgroundColor: ['#ec4899', '#14b8a6', '#fbbf24'][i % 3]
                            }}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-amber-500 to-pink-500 rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Join Our Mission
            </h3>
            <p className="text-xl text-white/90 mb-6 max-w-2xl mx-auto">
              We supply delicious doughnuts that help you raise funds for your own cause. Join our growing network of charities and communities making a difference, one box at a time.
            </p>
            <div className="flex justify-center space-x-3">
              {[...Array(7)].map((_, i) => (
                <span
                  key={i}
                  className="inline-block w-3 h-3 rounded-full bg-white animate-pulse"
                  style={{
                    animationDelay: `${i * 0.2}s`
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;